import { CircularProgress, Backdrop, Box } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IRequestType } from '../../constants/interfaces';
import { useStyles } from './HomePage.styles';
import HandbookDialog from './HandbookDialog';
import { useAccount } from '../../App';
import { useRequestTypesContext } from '../../contexts/RequestTypesContext';
import { useHandbookDialog } from './hooks/useHandbookDialog';
import { useLayout } from '../../contexts/LayoutContext';
import DashboardView from './components/DashboardView';

export default function HomePage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    sortedRequestTypes,
    loading: requestLoading,
    error,
  } = useRequestTypesContext();
  const { activeAccount } = useAccount();
  const { setSelectedRequestType } = useLayout();

  // Use the custom dialog hook
  const {
    dialogState,
    showHandbookDialog,
    openHandbook,
    closeHandbook,
    setHandbookChecked,
  } = useHandbookDialog();

  // Combined loading state
  const isLoading = requestLoading;

  // Clear selected request type when HomePage mounts
  useEffect(() => {
    setSelectedRequestType(null);
  }, [setSelectedRequestType]);

  // Handle MenuItem click to open handbook or navigate to request page
  const handleMenuItemClick = useCallback(
    (type: IRequestType) => {
      if (type.handbook_content) {
        openHandbook(type);
      } else {
        navigate(`request/${type.id}`);
      }
    },
    [openHandbook, navigate]
  );

  // Accept handler
  const handleAcceptHandbook = useCallback(() => {
    if (dialogState.pendingRequestType) {
      navigate(`request/${dialogState.pendingRequestType.id}`);
    }
    closeHandbook();
  }, [dialogState.pendingRequestType, closeHandbook, navigate]);

  // Cancel handler
  const handleCancelHandbook = useCallback(() => {
    closeHandbook();
  }, [closeHandbook]);

  return (
    <>
      <Backdrop
        open={isLoading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      {error && (
        <Box
          className={classes.errorCenter}
          sx={{ borderRadius: 1, color: 'error.dark', p: 2 }}
        >
          {error}
        </Box>
      )}

      {!error && (
        <DashboardView
          classes={classes}
          sortedRequestTypes={sortedRequestTypes}
          onMenuItemClick={handleMenuItemClick}
          activeAccount={activeAccount}
        />
      )}

      <HandbookDialog
        open={showHandbookDialog}
        handbookContent={dialogState.pendingRequestType?.handbook_content || ''}
        checked={dialogState.handbookChecked}
        onCheck={setHandbookChecked}
        onAccept={handleAcceptHandbook}
        onCancel={handleCancelHandbook}
      />
    </>
  );
}
